import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, 
  Plus, 
  Search, 
  LogOut, 
  Calendar, 
  Clock, 
  User, 
  Phone, 
  FileText, 
  X, 
  AlertCircle, 
  CheckCircle2, 
  ChevronRight, 
  ChevronLeft, 
  RefreshCw,
  Weight,
  Stethoscope,
  Tag,
  ShieldAlert,
  FileCheck,
  Upload,
  ExternalLink,
  Sun,
  Moon,
  Wrench
} from 'lucide-react';
import { supabase } from '../supabaseClient';
import { useNotifications } from '../context/NotificationContext';
import { useTheme } from '../context/ThemeContext';
import { MAINTENANCE_CONFIG } from '../config/maintenance';
import Modal from '../components/Modal';
import './CompanyPortal.css';

const getLocalISODate = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const CompanyPortal = () => {
  const navigate = useNavigate();
  const { addNotification } = useNotifications();
  const { theme, toggleTheme } = useTheme();

  // Active Company Session
  const [activeCompany, setActiveCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [appointments, setAppointments] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  // Logout Modal
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  // Detail Modal State (Matching Image 2)
  const [selectedDetailApp, setSelectedDetailApp] = useState(null);
  const [isDetailUploading, setIsDetailUploading] = useState(false);

  const handleUploadDetailFile = async (e) => {
    if (!selectedDetailApp) return;
    const file = e.target.files[0];
    if (!file) return;

    setIsDetailUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `company-${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

      let publicUrl = '';
      const { error: uploadError } = await supabase.storage.from('patient-docs').upload(fileName, file);
      if (uploadError) {
        publicUrl = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(file);
        });
      } else {
        const { data: { publicUrl: url } } = supabase.storage.from('patient-docs').getPublicUrl(fileName);
        publicUrl = url;
      }

      const existingFiles = selectedDetailApp.study_details?.attached_files || [];
      const updatedFiles = [...existingFiles, { name: file.name, url: publicUrl, study_name: selectedDetailApp.study_type || 'Orden Médica' }];

      const updatedStudyDetails = {
        ...(selectedDetailApp.study_details || {}),
        attached_files: updatedFiles
      };

      const { error: updateErr } = await supabase
        .from('appointments')
        .update({ study_details: updatedStudyDetails })
        .eq('id', selectedDetailApp.id);

      if (updateErr) throw updateErr;

      setSelectedDetailApp(prev => ({
        ...prev,
        study_details: updatedStudyDetails
      }));

      addNotification({ title: 'Éxito', message: 'Archivo adjuntado correctamente al turno.', type: 'success' });
      fetchCompanyData();
    } catch (err) {
      console.error('Error uploading detail file:', err);
      addNotification({ title: 'Error', message: 'No se pudo adjuntar el archivo', type: 'error' });
    } finally {
      setIsDetailUploading(false);
    }
  };

  // Form Modal State
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [step, setStep] = useState(1); // 1: Patient, 2: Studies & Slot, 3: Review/Submit
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Predefined & Custom Studies List
  const [availableStudiesList, setAvailableStudiesList] = useState([]);
  const [studySearch, setStudySearch] = useState('');
  const [customStudyInput, setCustomStudyInput] = useState('');
  const [selectedStudies, setSelectedStudies] = useState([]);

  // Professionals List for slot selection
  const [professionalsList, setProfessionalsList] = useState([]);

  // Attached files for medical orders
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  const handleFileUploadForStudy = async (e, studyName) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `company-${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      
      let publicUrl = '';
      const { error: uploadError } = await supabase.storage.from('patient-docs').upload(fileName, file);
      if (uploadError) {
        // Fallback to Base64 data URL
        publicUrl = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(file);
        });
      } else {
        const { data: { publicUrl: url } } = supabase.storage.from('patient-docs').getPublicUrl(fileName);
        publicUrl = url;
      }
      
      setAttachedFiles(prev => [...prev, { name: file.name, url: publicUrl, study_name: studyName }]);
      addNotification({ title: 'Éxito', message: `Orden médica cargada para ${studyName}`, type: 'success' });
    } catch (err) {
      console.error(err);
      addNotification({ title: 'Error', message: 'No se pudo subir el archivo', type: 'error' });
    } finally {
      setIsUploading(false);
    }
  };

  const removeAttachedFile = (fileName) => {
    setAttachedFiles(prev => prev.filter(f => f.name !== fileName));
  };

  // Patient autocomplete suggestions
  const [patientSuggestions, setPatientSuggestions] = useState([]);
  const [isSearchingPatient, setIsSearchingPatient] = useState(false);
  const [showPatientSuggestions, setShowPatientSuggestions] = useState(false);

  const searchExistingPatients = async (query) => {
    if (!query || query.trim().length < 2) {
      setPatientSuggestions([]);
      setShowPatientSuggestions(false);
      return;
    }

    setIsSearchingPatient(true);
    setShowPatientSuggestions(true);

    try {
      const clean = query.replace(/[,()"\\]/g, ' ').trim();
      if (!clean) {
        setPatientSuggestions([]);
        return;
      }
      const isNumeric = /^\d+$/.test(clean);

      let queryBuilder = supabase.from('patients').select('*');
      if (isNumeric) {
        queryBuilder = queryBuilder.ilike('dni', `%${clean}%`);
      } else {
        const terms = clean.split(/\s+/).filter(Boolean);
        const andFullName = `and(${terms.map(t => `full_name.ilike.%${t}%`).join(',')})`;
        queryBuilder = queryBuilder.or(`${andFullName},dni.ilike.%${clean}%`);
      }

      const { data, error } = await queryBuilder.limit(5);
      if (!error && data) {
        setPatientSuggestions(data);
      }
    } catch (err) {
      console.error('Error searching patients:', err);
    } finally {
      setIsSearchingPatient(false);
    }
  };

  const handleSelectPatientSuggestion = (patient) => {
    setFormData(prev => ({
      ...prev,
      patient_name: patient.full_name || prev.patient_name,
      patient_dni: patient.dni || prev.patient_dni,
      patient_birthdate: patient.birthdate || prev.patient_birthdate,
      patient_phone: patient.phone || prev.patient_phone,
      patient_weight: patient.weight || prev.patient_weight || ''
    }));
    setShowPatientSuggestions(false);
    setPatientSuggestions([]);
    addNotification({ title: 'Paciente encontrado', message: `Datos de ${patient.full_name} precargados.`, type: 'info' });
  };

  // Form Fields
  const [formData, setFormData] = useState({
    // Step 1: Patient Data
    patient_name: '',
    patient_dni: '',
    patient_birthdate: '',
    patient_phone: '',
    patient_weight: '',

    // Step 3: Request details
    notes: ''
  });

  // Verify Company Session
  useEffect(() => {
    const sessionStr = localStorage.getItem('gdt_active_company');
    if (!sessionStr) {
      navigate('/empresa/login');
      return;
    }
    try {
      const company = JSON.parse(sessionStr);
      if (!company || !company.id) {
        navigate('/empresa/login');
        return;
      }
      setActiveCompany(company);
    } catch (e) {
      console.error(e);
      navigate('/empresa/login');
    }
  }, [navigate]);

  // Fetch Appointments History & Master Data
  const fetchCompanyData = async () => {
    if (!activeCompany) return;
    setLoading(true);
    
    // 1. Fetch appointments requested by company
    try {
      const { data: appData, error: appErr } = await supabase
        .from('appointments')
        .select('*')
        .or(`created_by_user.ilike.%${activeCompany.name}%,admin_notes.ilike.%${activeCompany.name}%`)
        .order('appointment_date', { ascending: false });

      if (!appErr && appData) setAppointments(appData);
    } catch (e) {
      console.error('Error loading appointments history:', e);
    }

    // 2. Fetch Studies & Practices from 'services' table in Supabase
    try {
      const [servRes, studRes] = await Promise.all([
        supabase.from('services').select('*').order('name'),
        supabase.from('studies').select('*')
      ]);

      const allPractices = [];
      if (servRes.data) {
        servRes.data.forEach(s => {
          const n = (s.name || s.title || '').trim();
          if (n && !allPractices.includes(n)) allPractices.push(n);
        });
      }
      if (studRes.data) {
        studRes.data.forEach(s => {
          const n = (s.name || s.title || '').trim();
          if (n && !allPractices.includes(n)) allPractices.push(n);
        });
      }
      setAvailableStudiesList(allPractices);
    } catch (e) {
      console.error('Error loading services and practices:', e);
    }

    // 3. Fetch Professionals
    try {
      const { data: profData } = await supabase.from('professionals').select('*').eq('is_hidden', false);
      if (profData && profData.length > 0) {
        setProfessionalsList(profData);
      }
    } catch (e) {
      console.error('Error loading professionals:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeCompany) {
      fetchCompanyData();
    }
  }, [activeCompany]);

  const handleLogout = () => {
    localStorage.removeItem('gdt_active_company');
    navigate('/empresa/login');
  };

  const handleOpenForm = () => {
    setStep(1);
    setSelectedStudies([]);
    setFormData({
      patient_name: '',
      patient_dni: '',
      patient_birthdate: '',
      patient_phone: '',
      patient_weight: '',
      notes: ''
    });
    setIsFormOpen(true);
  };

  const handleAddStudy = (studyName) => {
    const trimmed = studyName.trim();
    if (trimmed && !selectedStudies.includes(trimmed)) {
      setSelectedStudies([...selectedStudies, trimmed]);
      setStudySearch('');
      setCustomStudyInput('');
    }
  };

  const handleRemoveStudy = (studyName) => {
    setSelectedStudies(selectedStudies.filter(s => s !== studyName));
  };

  // Submit appointment helper
  const submitAppointment = async ({ keepPatient = false }) => {
    if (!formData.patient_name.trim() || !formData.patient_dni.trim() || !formData.patient_phone.trim() || !formData.patient_weight.trim()) {
      addNotification({ title: 'Atención', message: 'Nombre, DNI, Teléfono y Peso del paciente son obligatorios.', type: 'warning' });
      setStep(1);
      return;
    }

    if (selectedStudies.length === 0) {
      addNotification({ title: 'Atención', message: 'Debe seleccionar al menos un estudio o consulta.', type: 'warning' });
      setStep(2);
      return;
    }

    setIsSubmitting(true);
    try {
      const studyTypeString = selectedStudies.join(', ');
      const operatorString = `Empresa: ${activeCompany.name}`;

      const payload = {
        patient_name: formData.patient_name.trim(),
        patient_dni: formData.patient_dni.trim(),
        patient_phone: formData.patient_phone.trim(),
        patient_email: activeCompany.email || 'empresa@gdt.com',
        appointment_date: getLocalISODate(new Date()),
        appointment_time: '00:00:00', // Unassigned time -> goes to "Turnos por Otorgar / Asignar" inbox instead of grid
        status: 'pendiente',
        study_type: studyTypeString,
        professional_name: 'CONSULTORIOS EXTERNOS',
        admin_notes: `[Solicitud Empresa: ${activeCompany.name}] ${formData.notes ? `- ${formData.notes}` : ''}`,
        created_by_user: operatorString,
        study_details: {
          patient_weight: formData.patient_weight ? `${formData.patient_weight} kg` : '',
          patient_birthdate: formData.patient_birthdate || '',
          requested_by_company_id: activeCompany.id,
          requested_by_company_name: activeCompany.name,
          requested_by_company_email: activeCompany.email,
          insurance_provider: activeCompany.name,
          insurance_plan: activeCompany.email,
          attached_files: attachedFiles
        }
      };

      // 1. Save or sync patient in patients table
      let targetPatientId = null;
      if (formData.patient_dni.trim()) {
        const { data: existingPatient } = await supabase
          .from('patients')
          .select('id')
          .eq('dni', formData.patient_dni.trim())
          .maybeSingle();

        if (existingPatient?.id) {
          targetPatientId = existingPatient.id;
        } else {
          const { data: createdP } = await supabase.from('patients').insert([{
            full_name: formData.patient_name.trim().toUpperCase(),
            dni: formData.patient_dni.trim(),
            phone: formData.patient_phone.trim(),
            insurance_provider: activeCompany.name,
            insurance_plan: activeCompany.email
          }]).select('id').maybeSingle();
          if (createdP?.id) targetPatientId = createdP.id;
        }
      }

      if (targetPatientId) {
        payload.patient_id = targetPatientId;
      }

      // 2. Insert Appointment
      const { error: insErr } = await supabase.from('appointments').insert([payload]);
      if (insErr) throw insErr;

      addNotification({ 
        title: 'Turno Solicitado', 
        message: `Turno de ${studyTypeString} asignado a ${formData.patient_name}.`, 
        type: 'success' 
      });

      fetchCompanyData();

      if (keepPatient) {
        // Keep patient data, reset step 2 & 3 to book another turn for same patient
        setSelectedStudies([]);
        setFormData(prev => ({
          ...prev,
          notes: ''
        }));
        setStep(2); // Go to step 2 for new study selection
      } else {
        setIsFormOpen(false);
      }
    } catch (err) {
      console.error('Error submitting company appointment:', err);
      addNotification({ title: 'Error al solicitar turno', message: err.message, type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredAppointments = appointments.filter(app => {
    const q = searchQuery.toLowerCase();
    return (
      (app.patient_name || '').toLowerCase().includes(q) ||
      (app.patient_dni || '').toLowerCase().includes(q) ||
      (app.study_type || '').toLowerCase().includes(q) ||
      (app.appointment_date || '').toLowerCase().includes(q)
    );
  });

  if (!activeCompany) return null;

  const companyInitials = activeCompany.name ? activeCompany.name.substring(0, 2).toUpperCase() : 'EM';

  if (MAINTENANCE_CONFIG.enabled) {
    return (
      <div className="company-portal-container page-animate" style={{ position: 'relative', overflow: 'hidden' }}>
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          zIndex: 999999,
          background: 'rgba(10, 15, 30, 0.96)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          userSelect: 'none'
        }}>
          <div style={{
            maxWidth: '540px',
            width: '100%',
            background: 'rgba(18, 24, 43, 0.96)',
            border: '1px solid rgba(0, 229, 255, 0.35)',
            borderRadius: '24px',
            padding: '40px 32px',
            textAlign: 'center',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.85), 0 0 35px rgba(0, 229, 255, 0.15)',
            color: '#ffffff'
          }}>
            <div style={{
              width: '76px',
              height: '76px',
              borderRadius: '50%',
              background: 'rgba(0, 229, 255, 0.12)',
              border: '1px solid rgba(0, 229, 255, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px auto',
              color: '#00e5ff'
            }}>
              <Wrench size={38} />
            </div>

            <h2 style={{ fontSize: '1.6rem', fontWeight: '700', color: '#00e5ff', marginBottom: '14px', letterSpacing: '-0.5px' }}>
              Portal Empresas en Mantenimiento
            </h2>

            <p style={{ fontSize: '0.98rem', color: '#cbd5e1', lineHeight: '1.6', marginBottom: '20px' }}>
              Estimada institución / empresa: El formulario de gestión y solicitud de turnos se encuentra momentáneamente deshabilitado por tareas de mantenimiento.
            </p>

            <div style={{
              background: 'rgba(0, 229, 255, 0.06)',
              borderLeft: '4px solid #00e5ff',
              padding: '14px 18px',
              borderRadius: '10px',
              textAlign: 'left',
              fontSize: '0.88rem',
              color: '#94a3b8',
              lineHeight: '1.5',
              marginBottom: '28px'
            }}>
              <strong style={{ color: '#00e5ff' }}>ℹ️ Información sobre su atención:</strong><br />
              Por favor, intente nuevamente más tarde o comuníquese directamente con la administración para coordinar sus turnos corporativos.
            </div>

            <div style={{ fontSize: '0.82rem', color: '#64748b', fontWeight: '500' }}>
              GESTION DE TURNOS • Portal Empresas
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="company-portal-container page-animate">
      {/* Top Navbar */}
      <header className="company-portal-header">
        <div className="brand-box">
          <div className="brand-icon"><Building2 size={24} color="var(--primary-cyan)" /></div>
          <div>
            <h2>GDT | PORTAL DE EMPRESAS</h2>
            <span className="brand-sub">Sistema de Solicitud e Historial de Turnos</span>
          </div>
        </div>

        <div className="company-user-badge">
          <div className="company-avatar-circle">{companyInitials}</div>
          <div className="company-user-info">
            <span className="company-title">{activeCompany.name}</span>
            <span className="company-email">{activeCompany.email}</span>
          </div>
          <button 
            type="button"
            className="btn-theme-portal"
            onClick={toggleTheme}
            title={theme === 'dark' ? 'Cambiar a Modo Claro' : 'Cambiar a Modo Oscuro'}
            style={{
              background: 'rgba(255,255,255,0.08)',
              border: '1px solid var(--border-glass)',
              color: 'var(--text-primary)',
              padding: '0.45rem 0.85rem',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
              fontSize: '0.8rem',
              fontWeight: '600',
              cursor: 'pointer',
              marginLeft: '0.5rem'
            }}
          >
            {theme === 'dark' ? <Sun size={16} color="#ffca28" /> : <Moon size={16} color="#00838f" />}
            <span>{theme === 'dark' ? 'Modo Claro ☀️' : 'Modo Oscuro 🌙'}</span>
          </button>
          <button 
            className="btn-logout-portal"
            onClick={() => setShowLogoutModal(true)}
            title="Cerrar Sesión"
          >
            <LogOut size={16} /> <span>SALIR</span>
          </button>
        </div>
      </header>

      {/* Main Dashboard Section */}
      <main className="company-portal-content">
        <div className="dashboard-control-bar glass-panel">
          <div className="search-input-box">
            <Search size={18} className="search-icon" />
            <input 
              type="text" 
              placeholder="Buscar paciente por Nombre, DNI, Estudio..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && <X size={16} className="clear-icon" onClick={() => setSearchQuery('')} />}
          </div>

          <button className="btn-primary-cyan" onClick={handleOpenForm}>
            <Plus size={20} /> <span>SOLICITAR NUEVO TURNO</span>
          </button>
        </div>

        {/* History Table */}
        <div className="history-section glass-panel">
          <div className="history-header">
            <h3>HISTORIAL DE TURNOS SOLICITADOS</h3>
            <span className="history-count">{filteredAppointments.length} Turnos registrados</span>
          </div>

          {loading ? (
            <div className="loading-state">
              <RefreshCw size={28} className="animate-spin" />
              <p>Cargando historial de turnos...</p>
            </div>
          ) : filteredAppointments.length === 0 ? (
            <div className="empty-history-state">
              <Calendar size={48} style={{ opacity: 0.2 }} />
              <p>No hay turnos solicitados registrados en esta cuenta.</p>
              <button className="btn-secondary-cyan" onClick={handleOpenForm}>
                <Plus size={16} /> Solicitar primer turno
              </button>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="portal-table">
                <thead>
                  <tr>
                    <th>FECHA Y HORA</th>
                    <th>PACIENTE & DNI</th>
                    <th>DATOS ADICIONALES</th>
                    <th>ESTUDIO / CONSULTA</th>
                    <th>PROFESIONAL</th>
                    <th>ESTADO</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAppointments.map(app => (
                    <tr 
                      key={app.id} 
                      onClick={() => setSelectedDetailApp(app)}
                      style={{ cursor: 'pointer' }}
                      title="Haga clic para ver detalles del paciente y archivos adjuntos"
                    >
                      <td>
                        <div className="date-cell">
                          <span className="date-main">📅 {app.appointment_date}</span>
                          <span className="time-main">
                            ⏰ {app.appointment_time && app.appointment_time !== '00:00:00' 
                              ? `${app.appointment_time.substring(0, 5)} hs` 
                              : (app.study_details?.preferred_time ? `Pref: ${app.study_details.preferred_time.substring(0, 5)} hs (Por Asignar)` : 'Por Asignar')}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="patient-cell">
                          <span className="p-name" style={{ color: 'var(--primary-cyan)' }}>{app.patient_name}</span>
                          <span className="p-dni">DNI: {app.patient_dni || 'Sin DNI'}</span>
                        </div>
                      </td>
                      <td>
                        <div className="details-cell">
                          {app.patient_phone && <span>📞 {app.patient_phone}</span>}
                          {app.study_details?.patient_weight && <span>⚖️ Peso: {app.study_details.patient_weight}</span>}
                        </div>
                      </td>
                      <td>
                        <span className="study-badge">🩺 {app.study_type || 'Consulta General'}</span>
                      </td>
                      <td>
                        <span className="prof-name">{app.professional_name || 'Sin asignar'}</span>
                      </td>
                      <td>
                        <span className={`status-tag ${app.status || 'pendiente'}`}>
                          {(app.status || 'pendiente').toUpperCase()}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* Patient Info & Attached Files Detail Modal (Matching Image 2) */}
      {selectedDetailApp && (
        <Modal 
          isOpen={true} 
          onClose={() => setSelectedDetailApp(null)} 
          title="INFORMACIÓN Y ADJUNTOS DEL PACIENTE"
          maxWidth="640px"
        >
          <div className="patient-detail-modal-body">
            {/* Header Patient Card */}
            <div className="p-detail-header-card glass-panel">
              <div className="p-detail-header-top">
                <div>
                  <h3 className="p-detail-name">{selectedDetailApp.patient_name}</h3>
                  <span className="p-detail-dni">DNI: {selectedDetailApp.patient_dni || 'Sin DNI'}</span>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <span className="p-detail-study-tag">{selectedDetailApp.study_type}</span>
                  <div className="p-detail-time-status">
                    {selectedDetailApp.appointment_time && selectedDetailApp.appointment_time !== '00:00:00' 
                      ? `${selectedDetailApp.appointment_time.substring(0, 5)} hs` 
                      : '00:00 hs'} | <span className="status-gold">{(selectedDetailApp.status || 'PENDIENTE').toUpperCase()}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Info Grid Card */}
            <div className="p-detail-info-grid glass-panel">
              <div className="p-info-row">
                <div className="p-info-col">
                  <span className="lbl">Teléfono</span>
                  <div className="val flex-align-center" style={{ gap: '8px' }}>
                    <strong>{selectedDetailApp.patient_phone || 'Sin teléfono'}</strong>
                    {selectedDetailApp.patient_phone && (
                      <a 
                        href={`https://wa.me/${selectedDetailApp.patient_phone.replace(/\D/g, '')}`} 
                        target="_blank" 
                        rel="noreferrer" 
                        className="wa-badge-link"
                      >
                        📞 WhatsApp
                      </a>
                    )}
                  </div>
                </div>

                <div className="p-info-col">
                  <span className="lbl">Email</span>
                  <span className="val">{selectedDetailApp.patient_email || activeCompany.email}</span>
                </div>
              </div>

              <div className="p-info-row" style={{ marginTop: '0.75rem' }}>
                <div className="p-info-col">
                  <span className="lbl">Obra Social / Prepaga</span>
                  <span className="val"><strong>{activeCompany.name}</strong></span>
                </div>

                <div className="p-info-col">
                  <span className="lbl">Otorgado por</span>
                  <span className="val cyan">{selectedDetailApp.created_by_user || `Empresa: ${activeCompany.name}`}</span>
                </div>
              </div>

              {(selectedDetailApp.study_details?.patient_birthdate || selectedDetailApp.study_details?.patient_weight) && (
                <div className="p-info-row" style={{ marginTop: '0.75rem' }}>
                  {selectedDetailApp.study_details?.patient_birthdate && (
                    <div className="p-info-col">
                      <span className="lbl">Fecha de Nacimiento</span>
                      <span className="val">{selectedDetailApp.study_details.patient_birthdate}</span>
                    </div>
                  )}
                  {selectedDetailApp.study_details?.patient_weight && (
                    <div className="p-info-col">
                      <span className="lbl">Peso</span>
                      <span className="val">{selectedDetailApp.study_details.patient_weight}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Attached Files & Medical Orders Section (Matching Image 2) */}
            <div className="p-detail-attached-section glass-panel">
              <div className="attached-section-header">
                <div className="section-title">
                  <FileText size={16} color="#00e5ff" /> 
                  <span>ARCHIVOS ADJUNTOS Y ÓRDENES MÉDICAS ({(selectedDetailApp.study_details?.attached_files || []).length})</span>
                </div>

                <label className="btn-upload-attached-green">
                  {isDetailUploading ? <RefreshCw className="animate-spin" size={14} /> : <Plus size={14} />}
                  <span>+ SUBIR ADJUNTO</span>
                  <input 
                    type="file" 
                    accept="image/*,application/pdf,.doc,.docx"
                    style={{ display: 'none' }} 
                    onChange={handleUploadDetailFile} 
                    disabled={isDetailUploading}
                  />
                </label>
              </div>

              <div className="attached-files-list-box">
                {(!selectedDetailApp.study_details?.attached_files || selectedDetailApp.study_details.attached_files.length === 0) ? (
                  <div className="empty-attached-msg">
                    <p>No hay archivos u órdenes médicas adjuntas en este turno.</p>
                  </div>
                ) : (
                  selectedDetailApp.study_details.attached_files.map((file, fIdx) => (
                    <div key={fIdx} className="attached-file-item-card">
                      <div className="file-info-col">
                        <FileText size={16} className="file-icon-cyan" />
                        <span className="file-title-name">{file.name}</span>
                      </div>
                      <a 
                        href={file.url} 
                        target="_blank" 
                        rel="noreferrer" 
                        className="btn-open-download"
                      >
                        <ExternalLink size={14} /> ABRIR / DESCARGAR
                      </a>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="detail-modal-footer">
              <button className="btn-modal-close-gray" onClick={() => setSelectedDetailApp(null)}>
                CERRAR
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal Logout Confirmation */}
      {showLogoutModal && (
        <div className="appointment-modal-overlay">
          <div className="appointment-modal-container glass-modal-premium" style={{ maxWidth: '420px', textAlign: 'center' }}>
            <div className="logout-modal-content" style={{ padding: '2rem 1.5rem 1rem 1.5rem' }}>
              <div className="logout-icon-wrapper" style={{ margin: '0 auto 1rem auto', width: '56px', height: '56px', borderRadius: '50%', background: 'rgba(255,82,82,0.12)', border: '1px solid rgba(255,82,82,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ff5252' }}>
                <LogOut size={26} />
              </div>
              <h3 style={{ color: 'white', margin: '0 0 0.5rem 0', fontSize: '1.2rem' }}>CERRAR SESIÓN</h3>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', margin: 0 }}>
                ¿Estás seguro que deseas salir del Portal de Empresas?
              </p>
            </div>

            <div className="appointment-modal-footer" style={{ justifyContent: 'center', gap: '1rem', padding: '1rem 1.5rem 1.5rem 1.5rem' }}>
              <button className="btn-cancel-modal" onClick={() => setShowLogoutModal(false)}>CANCELAR</button>
              <button className="btn-save-appointment" style={{ background: '#ff5252', borderColor: '#ff5252', color: 'white' }} onClick={handleLogout}>
                SÍ, CERRAR SESIÓN
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Multi-Step Appointment Request Form Modal */}
      {isFormOpen && (
        <div className="appointment-modal-overlay">
          <div className="appointment-modal-container glass-modal-premium" style={{ maxWidth: '680px' }}>
            {/* Header */}
            <div className="appointment-modal-header premium-header">
              <div className="header-title">
                <div className="title-icon-wrapper cyan"><Building2 size={20} /></div>
                <span>SOLICITAR NUEVO TURNO - EMPRESA</span>
              </div>
              <button className="btn-close-modal" onClick={() => setIsFormOpen(false)}><X size={20} /></button>
            </div>

            {/* Steps Indicator Bar */}
            <div className="steps-indicator-bar">
              <div className={`step-item ${step === 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`} onClick={() => setStep(1)}>
                <span className="step-num">1</span>
                <span className="step-txt">Datos del Paciente</span>
              </div>
              <div className="step-connector"></div>
              <div className={`step-item ${step === 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`} onClick={() => step > 1 && setStep(2)}>
                <span className="step-num">2</span>
                <span className="step-txt">Estudios & Slot</span>
              </div>
              <div className="step-connector"></div>
              <div className={`step-item ${step === 3 ? 'active' : ''}`} onClick={() => step > 2 && setStep(3)}>
                <span className="step-num">3</span>
                <span className="step-txt">Datos de Solicitud</span>
              </div>
            </div>

            {/* Modal Body per Step */}
            <div className="appointment-modal-body" style={{ minHeight: '340px' }}>
              {/* STEP 1: PATIENT DATA */}
              {step === 1 && (
                <div className="step-content page-animate">
                  <div className="form-grid-2">
                    <div className="form-group full-width" style={{ position: 'relative' }}>
                      <label>NOMBRE Y APELLIDO DEL PACIENTE *</label>
                      <input 
                        type="text" 
                        required
                        placeholder="Ej: Perez, Juan Carlos (escriba para buscar paciente existente)"
                        value={formData.patient_name}
                        onChange={(e) => {
                          const val = e.target.value;
                          setFormData({ ...formData, patient_name: val });
                          searchExistingPatients(val);
                        }}
                      />
                    </div>

                    <div className="form-group" style={{ position: 'relative' }}>
                      <label>DNI DEL PACIENTE *</label>
                      <input 
                        type="text" 
                        required
                        placeholder="Sin puntos, Ej: 35123456"
                        value={formData.patient_dni}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, '');
                          setFormData({ ...formData, patient_dni: val });
                          searchExistingPatients(val);
                        }}
                      />
                    </div>

                    {/* Real-time Patient Autocomplete Suggestions */}
                    {showPatientSuggestions && patientSuggestions.length > 0 && (
                      <div className="patient-suggestions-box glass-panel full-width" style={{ marginTop: '-0.5rem', marginBottom: '1rem', width: '100%', gridColumn: '1 / -1' }}>
                        <div className="suggestions-header-title">
                          <User size={14} /> PACIENTES REGISTRADOS EN SISTEMA (Haga clic para autocompletar):
                        </div>
                        <div className="suggestions-list">
                          {patientSuggestions.map(p => (
                            <div 
                              key={p.id} 
                              className="patient-suggestion-row"
                              onClick={() => handleSelectPatientSuggestion(p)}
                            >
                              <div className="p-icon"><User size={16} /></div>
                              <div className="p-info">
                                <span className="p-name">{p.full_name}</span>
                                <span className="p-meta">
                                  DNI: <strong>{p.dni || 'Sin DNI'}</strong> 
                                  {p.birthdate ? ` | Nac: ${p.birthdate}` : ''} 
                                  {p.phone ? ` | Tel: ${p.phone}` : ''}
                                </span>
                              </div>
                              <button type="button" className="btn-select-patient">USAR DATOS</button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="form-group">
                      <label>FECHA DE NACIMIENTO (DD/MM/AAAA)</label>
                      <input 
                        type="text" 
                        placeholder="Ej: 15/08/1990"
                        value={formData.patient_birthdate}
                        onChange={(e) => setFormData({ ...formData, patient_birthdate: e.target.value })}
                      />
                    </div>

                    <div className="form-group">
                      <label>TELÉFONO DEL PACIENTE * (Recordatorios WhatsApp)</label>
                      <input 
                        type="text" 
                        required
                        placeholder="Ej: 1199887766"
                        value={formData.patient_phone}
                        onChange={(e) => setFormData({ ...formData, patient_phone: e.target.value })}
                      />
                    </div>

                    <div className="form-group">
                      <label>PESO DEL PACIENTE (KG) *</label>
                      <input 
                        type="text" 
                        required
                        placeholder="Ej: 75 kg"
                        value={formData.patient_weight}
                        onChange={(e) => setFormData({ ...formData, patient_weight: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 2: STUDIES SELECTION & SLOT */}
              {step === 2 && (
                <div className="step-content page-animate">
                  <div className="form-group full-width">
                    <label>SELECCIÓN DE ESTUDIOS / CONSULTAS *</label>
                    <p className="field-hint">Busque en el listado o escriba un estudio personalizado:</p>
                    
                    <div className="study-search-box">
                      <input 
                        type="text"
                        placeholder="Escriba para buscar o agregar una práctica/estudio..."
                        value={studySearch}
                        onChange={(e) => setStudySearch(e.target.value)}
                        onFocus={() => {
                          const dropdown = document.getElementById('company-study-dropdown');
                          if (dropdown) dropdown.style.display = 'block';
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            if (studySearch.trim()) handleAddStudy(studySearch);
                          }
                        }}
                      />
                      {studySearch.trim() && (
                        <button 
                          type="button" 
                          className="btn-add-custom-study"
                          onClick={() => handleAddStudy(studySearch)}
                        >
                          <Plus size={14} /> AGREGAR "{studySearch.trim()}"
                        </button>
                      )}
                    </div>

                    {/* Filtered Dropdown Suggestions from 'Estudios y Prácticas' */}
                    <div id="company-study-dropdown" className="study-suggestions-dropdown" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                      <div style={{ padding: '6px 12px', fontSize: '0.72rem', color: 'var(--text-muted)', background: 'rgba(0,0,0,0.4)', fontWeight: 'bold' }}>
                        PRÁCTICAS DISPONIBLES EN SISTEMA ({availableStudiesList.length})
                      </div>
                      {availableStudiesList
                        .filter(s => !studySearch.trim() || s.toLowerCase().includes(studySearch.toLowerCase()))
                        .map((st, i) => (
                          <div 
                            key={i} 
                            className="suggestion-item" 
                            onClick={() => {
                              handleAddStudy(st);
                            }}
                          >
                            <Stethoscope size={14} style={{ color: 'var(--primary-cyan)' }} /> 
                            <span style={{ fontWeight: 600 }}>{st}</span>
                          </div>
                        ))
                      }
                      {studySearch.trim() && !availableStudiesList.some(s => s.toLowerCase() === studySearch.toLowerCase().trim()) && (
                        <div 
                          className="suggestion-item custom-add" 
                          onClick={() => handleAddStudy(studySearch)}
                          style={{ color: '#00e5ff', background: 'rgba(0,229,255,0.08)' }}
                        >
                          <Plus size={14} /> <span>Agregar estudio personalizado: "<strong>{studySearch.trim()}</strong>"</span>
                        </div>
                      )}
                    </div>

                    {/* Selected Studies Cards with File Upload (Matching Image 2) */}
                    <div className="selected-studies-cards-list" style={{ marginTop: '1.25rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                      {selectedStudies.length === 0 ? (
                        <div className="no-studies-badge" style={{ textAlign: 'center', padding: '2rem', background: 'rgba(255,255,255,0.02)', borderRadius: '14px', color: 'var(--text-muted)' }}>
                          <Stethoscope size={32} style={{ opacity: 0.3, marginBottom: '0.5rem' }} />
                          <p style={{ margin: 0 }}>Ningún estudio seleccionado aún. Busque o escriba arriba para seleccionar.</p>
                        </div>
                      ) : (
                        selectedStudies.map((st, idx) => (
                          <div key={idx} className="study-card-selected glass-panel">
                            <div className="study-card-header-row">
                              <div className="study-card-title-box">
                                <h3 className="study-card-title">{st}</h3>
                                <span className="study-card-duration">⏱ 30 min</span>
                              </div>
                              <button type="button" className="btn-selected-pill">
                                SELECCIONADO
                              </button>
                            </div>

                            <div className="study-card-divider"></div>

                            <div className="study-card-upload-section">
                              <div className="upload-header-label">
                                <Upload size={16} /> <span>ADJUNTAR ORDEN MÉDICA</span>
                              </div>

                              {/* Uploaded files list for this study */}
                              {attachedFiles.filter(f => f.study_name === st).length > 0 && (
                                <div className="uploaded-files-list">
                                  {attachedFiles.filter(f => f.study_name === st).map((file, fIdx) => (
                                    <div key={fIdx} className="uploaded-file-chip">
                                      <FileText size={14} className="file-icon" />
                                      <a href={file.url} target="_blank" rel="noreferrer" className="file-name">{file.name}</a>
                                      <X size={14} className="btn-remove-file" onClick={() => removeAttachedFile(file.name)} title="Eliminar archivo" />
                                    </div>
                                  ))}
                                </div>
                              )}

                              <label className="study-upload-box">
                                {isUploading ? <RefreshCw className="animate-spin" size={18} /> : <Upload size={18} />}
                                <span>{isUploading ? 'Cargando archivo...' : 'Seleccionar archivo'}</span>
                                <input 
                                  type="file" 
                                  accept="image/*,application/pdf,.doc,.docx"
                                  style={{ display: 'none' }} 
                                  onChange={(e) => handleFileUploadForStudy(e, st)} 
                                  disabled={isUploading}
                                />
                              </label>
                            </div>

                            <button type="button" className="btn-remove-study-link" onClick={() => handleRemoveStudy(st)}>
                              <X size={14} /> Quitar estudio
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 3: CONTACT & REQUEST SUMMARY */}
              {step === 3 && (
                <div className="step-content page-animate">
                  <div className="summary-card-box">
                    <div className="summary-row">
                      <span className="s-label">Empresa Solicitante:</span>
                      <span className="s-val cyan">🏢 {activeCompany.name} ({activeCompany.email})</span>
                    </div>
                    <div className="summary-row">
                      <span className="s-label">Obra Social / Cobertura:</span>
                      <span className="s-val">{activeCompany.name}</span>
                    </div>
                    <div className="summary-row">
                      <span className="s-label">Paciente:</span>
                      <span className="s-val">👤 {formData.patient_name} (DNI: {formData.patient_dni})</span>
                    </div>
                    <div className="summary-row">
                      <span className="s-label">Teléfono Paciente:</span>
                      <span className="s-val">📞 {formData.patient_phone}</span>
                    </div>
                    {formData.patient_weight && (
                      <div className="summary-row">
                        <span className="s-label">Peso:</span>
                        <span className="s-val">⚖️ {formData.patient_weight} kg</span>
                      </div>
                    )}
                    <div className="summary-row">
                      <span className="s-label">Estudios:</span>
                      <span className="s-val yellow">🩺 {selectedStudies.join(', ')}</span>
                    </div>
                    <div className="summary-row">
                      <span className="s-label">Asignación de Turno:</span>
                      <span className="s-val green">📩 Pendiente (Bandeja de Recepción / Por Otorgar)</span>
                    </div>
                  </div>

                  <div className="form-group full-width" style={{ marginTop: '1rem' }}>
                    <label>OBSERVACIONES / NOTAS ADICIONALES PARA LA CLÍNICA</label>
                    <textarea 
                      rows={3}
                      placeholder="Ingrese indicaciones o notas especiales sobre el turno..."
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    />
                  </div>

                  {/* Medical Orders / Documents File Upload Section BELOW Observaciones */}
                  <div className="form-group full-width" style={{ marginTop: '1.25rem' }}>
                    <label style={{ color: '#00e5ff', display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: 'bold' }}>
                      <Upload size={16} /> ADJUNTAR ÓRDENES MÉDICAS Y ARCHIVOS
                    </label>
                    <p className="field-hint" style={{ marginBottom: '0.5rem' }}>
                      Cargue fotos o archivos PDF de la prescripción u orden médica del paciente:
                    </p>

                    {attachedFiles.length > 0 && (
                      <div className="uploaded-files-list" style={{ marginBottom: '0.75rem' }}>
                        {attachedFiles.map((file, fIdx) => (
                          <div key={fIdx} className="uploaded-file-chip">
                            <FileText size={14} className="file-icon" />
                            <a href={file.url} target="_blank" rel="noreferrer" className="file-name">{file.name}</a>
                            {file.study_name && (
                              <span className="f-study-tag" style={{ fontSize: '0.7rem', color: '#ffca28', background: 'rgba(255,202,40,0.1)', padding: '2px 6px', borderRadius: '4px' }}>
                                {file.study_name}
                              </span>
                            )}
                            <X size={14} className="btn-remove-file" onClick={() => removeAttachedFile(file.name)} title="Eliminar archivo" />
                          </div>
                        ))}
                      </div>
                    )}

                    <label className="study-upload-box">
                      {isUploading ? <RefreshCw className="animate-spin" size={18} /> : <Upload size={18} />}
                      <span>{isUploading ? 'Subiendo orden médica...' : 'Seleccionar archivo (PDF o Imagen de Orden Médica)'}</span>
                      <input 
                        type="file" 
                        accept="image/*,application/pdf,.doc,.docx"
                        style={{ display: 'none' }} 
                        onChange={(e) => handleFileUploadForStudy(e, selectedStudies.join(', ') || 'Orden Médica')} 
                        disabled={isUploading}
                      />
                    </label>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer Controls */}
            <div className="appointment-modal-footer" style={{ gap: '0.75rem', flexWrap: 'wrap' }}>
              {step > 1 && (
                <button type="button" className="btn-cancel-modal" onClick={() => setStep(step - 1)}>
                  <ChevronLeft size={16} /> ANTERIOR
                </button>
              )}

              {step < 3 ? (
                <button 
                  type="button" 
                  className="btn-save-appointment"
                  onClick={() => {
                    if (step === 1) {
                      if (!formData.patient_name.trim() || !formData.patient_dni.trim() || !formData.patient_phone.trim() || !formData.patient_weight.trim()) {
                        addNotification({ title: 'Atención', message: 'Complete los campos obligatorios del paciente (Nombre, DNI, Teléfono y Peso).', type: 'warning' });
                        return;
                      }
                    }
                    if (step === 2) {
                      if (selectedStudies.length === 0) {
                        addNotification({ title: 'Atención', message: 'Seleccione al menos un estudio.', type: 'warning' });
                        return;
                      }
                    }
                    setStep(step + 1);
                  }}
                >
                  SIGUIENTE <ChevronRight size={16} />
                </button>
              ) : (
                <>
                  <button 
                    type="button" 
                    className="btn-save-appointment"
                    style={{ background: 'rgba(0, 229, 255, 0.15)', borderColor: 'var(--primary-cyan)', color: '#00e5ff' }}
                    disabled={isSubmitting}
                    onClick={() => submitAppointment({ keepPatient: true })}
                  >
                    <Plus size={16} /> ASIGNAR OTRO TURNO MISMO PACIENTE
                  </button>

                  <button 
                    type="button" 
                    className="btn-save-appointment"
                    disabled={isSubmitting}
                    onClick={() => submitAppointment({ keepPatient: false })}
                  >
                    {isSubmitting ? 'SOLICITANDO...' : 'FINALIZAR'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyPortal;
